import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet-draw";
import AddressSearch from "./AddressSearch";

// Fix default marker icon paths (common issue with bundlers)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function DrawControl({ onBoundaryChange, initialBoundary = [] }) {
  const map = useMap();
  const drawnItemsRef = useRef(new L.FeatureGroup());

  useEffect(() => {
    const drawnItems = drawnItemsRef.current;
    map.addLayer(drawnItems);

    // Pre-load an existing polygon if one was passed in
    if (initialBoundary.length > 0) {
      const latlngs = initialBoundary.map((p) => [p.lat, p.lng]);
      const existingPolygon = L.polygon(latlngs, { color: "#2563eb" });
      drawnItems.addLayer(existingPolygon);
      map.fitBounds(existingPolygon.getBounds(), { padding: [40, 40] });
    }

    const drawControl = new L.Control.Draw({
      draw: {
        polygon: {
          allowIntersection: false,
          showArea: true,
          shapeOptions: { color: "#2563eb" },
        },
        // disable shapes you don't need
        polyline: false,
        rectangle: false,
        circle: false,
        marker: false,
        circlemarker: false,
      },
      edit: {
        featureGroup: drawnItems,
        remove: true,
      },
    });

    map.addControl(drawControl);

    const extractCoords = () => {
      const coords = [];
      drawnItems.eachLayer((layer) => {
        if (layer.getLatLngs) {
          // getLatLngs()[0] because polygons are nested arrays (rings)
          layer.getLatLngs()[0].forEach((latlng) => {
            coords.push({ lat: latlng.lat, lng: latlng.lng });
          });
        }
      });
      onBoundaryChange(coords);
    };

    const handleCreated = (e) => {
      drawnItems.addLayer(e.layer); // keep only one polygon at a time if desired
      extractCoords();
    };
    const handleEdited = () => extractCoords();
    const handleDeleted = () => extractCoords();

    map.on(L.Draw.Event.CREATED, handleCreated);
    map.on(L.Draw.Event.EDITED, handleEdited);
    map.on(L.Draw.Event.DELETED, handleDeleted);

    return () => {
      map.off(L.Draw.Event.CREATED, handleCreated);
      map.off(L.Draw.Event.EDITED, handleEdited);
      map.off(L.Draw.Event.DELETED, handleDeleted);
      map.removeControl(drawControl);
      map.removeLayer(drawnItems);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]); // run once on mount only — don't re-run this on every parent re-render

  return null;
}

export default function MapBoundaryDrawer({
  onBoundaryChange,
  initialBoundary = [],
  center,
  zoom = 12,
}) {
  const [boundary, setBoundary] = useState(initialBoundary);

  const handleBoundaryChange = (coords) => {
    setBoundary(coords);
    onBoundaryChange?.(coords); // send it up to parent / API call
  };

  // If an initial boundary was passed, center on its first point;
  // otherwise fall back to the default center
  const mapCenter =
    center ||
    (initialBoundary.length > 0
      ? [initialBoundary[0].lat, initialBoundary[0].lng]
      : [28.6139, 77.209]);

  return (
    <div>
      <MapContainer
        center={mapCenter}
        zoom={zoom}
        style={{ height: "500px", width: "100%" }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="&copy; OpenStreetMap contributors"
        />
        <AddressSearch
          onLocationSelect={(loc) => console.log("Selected:", loc)}
        />

        <DrawControl
          onBoundaryChange={handleBoundaryChange}
          initialBoundary={initialBoundary}
        />
      </MapContainer>

      {/* {boundary.length > 0 && (
        <pre
          style={{
            marginTop: 12,
            background: "#f4f4f5",
            padding: 12,
            borderRadius: 8,
          }}
        >
          {JSON.stringify(boundary, null, 2)}
        </pre>
      )} */}
    </div>
  );
}
